import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'
import { CheckboxValidators } from '@northfork/ng-basics'
import { of, Subject } from 'rxjs'
import { catchError, take, takeUntil, tap } from 'rxjs/operators'
import { MvtBasesInOut } from '../../../../model/mvt-basis-in-out.model'
import { Basis } from '../../../../model/mvt-basis.model'
import { DirectTransfer } from '../../../../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import {
  MarketValueTransferModel,
  MvtData,
} from '../../../../model/single-transaction/market-value-transfer.model'
import { TranCategory } from '../../../../model/transaction-category.model'
import { TransactionTypeKey } from '../../../../model/transaction-type.model'
import { CreateEditTransactionService } from '../../../../services/create-edit-transaction.service'

@Component({
  selector: 'hs-mvt-checkbox-control',
  template: ` <nf-form-control
    label="Market Value Transfer"
    *nfLoader="loadingBasis"
    class="market-value-transfer-div"
    compact
  >
    <nf-checkbox
      *ngFor="let option of suggestedMvtFormArray?.controls; let i = index"
      [formControl]="$any(option)"
      (blur)="onTouched()"
      label="{{ basis[i].prefix }}"
    >
    </nf-checkbox>
  </nf-form-control>`,
  styles: [
    `
      :host {
        display: block;
      }
      ::ng-deep .market-value-transfer-div .form-control-container {
        display: flex;
      }
      ::ng-deep nf-loader.active {
        z-index: 1;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MvtCheckboxControlComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MvtCheckboxControlComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input()
  sourceAccountId?: number
  @Input()
  destAccountId?: string
  // TODO this *may* get reused by other modals, in which case, the default
  // won't make sense.
  @Input()
  category: TranCategory = TranCategory.DIRECT_TRANSFER
  @Input()
  tranType?: TransactionTypeKey
  @Input()
  entryDate?: string
  @Input()
  editMode = false
  @Input()
  transaction?: DirectTransfer
  @Input()
  mvtDataMap?: { [key: number]: MvtData }
  @Input()
  destinationAccountId?: number

  @Output()
  basisUpdate = new EventEmitter<Basis[]>()

  @Output()
  inOutMvtBases = new EventEmitter<MvtBasesInOut>()

  @Output()
  errorDetails = new EventEmitter<unknown>()

  @Output()
  errorHeader = new EventEmitter<string>()

  basis: Basis[] = []
  suggestedMvtFormArray: FormArray = new FormArray([])
  loadingBasis = true

  updateMvtSubject = new Subject<void>()

  constructor(
    private readonly createEditTransactionService: CreateEditTransactionService,
  ) {}

  ngOnChanges(): void {
    this.updateMvtValues()
  }

  ngOnDestroy(): void {
    this.updateMvtSubject.next()
    this.updateMvtSubject.complete()
  }

  /** @internal */
  onChange = (value: Basis[]) => value

  /** @internal */
  onTouched = () => {}

  writeValue(value?: Basis[]): void {
    this.basis.forEach((basis, index) => {
      const isSelected = value?.some(b => b.basisId === basis.basisId)
      this.suggestedMvtFormArray.controls[index]?.setValue(isSelected)
    })
  }

  registerOnChange(fn: (value: Basis[]) => Basis[]): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  private updateMvtValues() {
    if (
      this.sourceAccountId == null ||
      this.tranType == null ||
      this.sourceAccountId == this.destinationAccountId
    ) {
      this.loadingBasis = false
      return
    }
    this.updateMvtSubject.next()
    this.loadingBasis = true
    const mvtData: MarketValueTransferModel = {
      category: this.category,
      accountId: this.sourceAccountId,
      tranType: this.tranType,
      destAccountId: this.destinationAccountId,
      entryDate: this.entryDate,
      tranId: this.transaction?.transactionId,
    }
    if (this.editMode && this.mvtDataMap) {
      const mvtTypeMap = Object.values<MvtData>(this.mvtDataMap)
      if (mvtTypeMap.length > 0) mvtData.mvtData = mvtTypeMap
    }
    this.createEditTransactionService
      .loadMvtValues(mvtData)
      .pipe(
        take(1),
        // Handle loading state
        tap(() => {
          this.loadingBasis = false
        }),
        // Emit new bases
        tap(mvtBasis => {
          this.basisUpdate.emit(mvtBasis.basis)
          this.inOutMvtBases?.emit(mvtBasis.mvtBases)
        }),
        // Update form controls
        tap(mvtBasis => {
          this.basis = mvtBasis.basis
          this.suggestedMvtFormArray = new FormArray(
            this.basis.map(o => {
              return new FormControl(o.checked)
            }),
            [CheckboxValidators.required],
          )
          this.onChange(this.getSelectedBasis())
          this.suggestedMvtFormArray.valueChanges
            .pipe(
              takeUntil(this.updateMvtSubject),
              tap(() => {
                this.onChange(this.getSelectedBasis())
              }),
            )
            .subscribe()
        }),
        catchError(error => {
          this.errorDetails.emit(error.error)
          this.errorHeader.emit('Error fetching Market Value Transfer')
          return of(null)
        }),
      )
      .subscribe()
  }

  private getSelectedBasis(): Basis[] {
    const selection: boolean[] = this.suggestedMvtFormArray.value
    return selection.reduce<Basis[]>((prevValue, basisSelected, index) => {
      if (basisSelected) {
        prevValue.push({ ...this.basis[index], checked: true })
      }
      return prevValue
    }, [])
  }
}
